import { useState, useRef } from 'react';
import { Box, Drawer, IconButton, Typography, LinearProgress, useMediaQuery, Dialog } from '@mui/material';
import Slider from 'react-slick';
import { useTheme } from '@mui/material/styles';
import Iconify from '../../components/Iconify';

import Bronze from '../../assets/vip/layout-bronze.png';
import Silver from '../../assets/vip/layout-silver.png';
import Gold from '../../assets/vip/layout-gold.png';
import Platinum from '../../assets/vip/layout-platinum.png';
import Diamond from '../../assets/vip/layout-diamond.png';
import Royal from '../../assets/vip/layout-royal.png';

import BronzeBackground from '../../assets/vip/bronze-background.png';
import SilverBackground from '../../assets/vip/silver-background.png';
import GoldBackground from '../../assets/vip/gold-background.png';
import PlatinumBackground from '../../assets/vip/platinum-background.png';
import DiamondBackground from '../../assets/vip/diamond-background.png';
import RoyalBackground from '../../assets/vip/royal-background.png';

import BronzeCardBackground from '../../assets/vip/card-bronze-background.png';
import SilverCardBackground from '../../assets/vip/card-silver-background.png';
import GoldCardBackground from '../../assets/vip/card-gold-background.png';
import PlatinumCardBackground from '../../assets/vip/card-platinum-background.png';
import DiamondCardBackground from '../../assets/vip/card-diamond-background.png';
import RoyalCardBackground from '../../assets/vip/card-royal-background.png';

import VipCarousel from './VipCarousel';
import useResponsive from '../../hooks/useResponsive';
import CustomModal from '../../components/CustomModal';
import useLocales from '../../hooks/useLocales';

const Vip = ({ handleOpen, handleClose }) => {
  const [progress] = useState(50);
  const [activeIndex, setActiveIndex] = useState(0);
  const isDesktop = useResponsive('up', 'lg');
  const { translate } = useLocales();

  const VIP_LEVELS = [
    {
      name: `${translate('bronze')}`,
      image: Bronze,
      bg: BronzeBackground,
      cardBackground: BronzeCardBackground,
      maintenanceRequirementValue: 100000,
      maintenanceRequirementMax: 50000,
      subColor: '#FFB39C',
      titleColor: ['#FFEFDC', '#FFF5F0', '#FF9C72'],
      details: {
        dailyRebate: '0.3',
        withdrawCount: '5',
        weeklyRebate: '2',
        tierBonus: '100',
        birthdayGift: `${translate('no')}`,
        birthdayBonus: '88',
        upgradeNext: '50,000',
        renewal: `${translate('lifetime')}`,
      },
    },
    {
      name: `${translate('silver')}`,
      image: Silver,
      bg: SilverBackground,
      cardBackground: SilverCardBackground,
      maintenanceRequirementValue: 60000,
      maintenanceRequirementMax: 100000,
      subColor: '#E7E7E7',
      titleColor: ['#FFFFFF', '#EBEBEB', '#B8B8B8'],
      details: {
        dailyRebate: '0.4',
        withdrawCount: '8',
        weeklyRebate: '2.5',
        tierBonus: '200',
        birthdayGift: `${translate('no')}`,
        birthdayBonus: '168',
        upgradeNext: '100,000',
        renewal: `3 ${translate('months')}`,
      },
    },
    {
      name: `${translate('gold')}`,
      image: Gold,
      bg: GoldBackground,
      cardBackground: GoldCardBackground,
      maintenanceRequirementValue: 120000,
      maintenanceRequirementMax: 150000,
      subColor: '#FFD19C',
      titleColor: ['#FFF9DC', '#FFF8F0', '#FFE89C'],
      details: {
        dailyRebate: '0.5',
        withdrawCount: '10',
        weeklyRebate: '3',
        tierBonus: '500',
        birthdayGift: `${translate('no')}`,
        birthdayBonus: '228',
        upgradeNext: '150,000',
        renewal: `3 ${translate('months')}`,
      },
    },
    {
      name: `${translate('platinum')}`,
      image: Platinum,
      bg: PlatinumBackground,
      cardBackground: PlatinumCardBackground,
      maintenanceRequirementValue: 100000,
      maintenanceRequirementMax: 200000,
      subColor: '#E7E7E7',
      titleColor: ['#FFFFFF', '#EBEBEB', '#B8B8B8'],
      details: {
        dailyRebate: '0.6',
        withdrawCount: '10',
        weeklyRebate: '3.5',
        tierBonus: '800',
        birthdayGift: `${translate('yes')}`,
        birthdayBonus: '688',
        upgradeNext: '200,000',
        renewal: `3 ${translate('months')}`,
      },
    },
    {
      name: `${translate('diamond')}`,
      image: Diamond,
      bg: DiamondBackground,
      cardBackground: DiamondCardBackground,
      maintenanceRequirementValue: 300000,
      maintenanceRequirementMax: 500000,
      subColor: '#D19CFF',
      titleColor: ['#EFDCFF', '#F8F0FF', '#D19CFF'],
      details: {
        dailyRebate: '0.7',
        withdrawCount: '20',
        weeklyRebate: '4',
        tierBonus: '1000',
        birthdayGift: `${translate('yes')}`,
        birthdayBonus: '888',
        upgradeNext: '500,000',
        renewal: `3 ${translate('months')}`,
      },
    },
    {
      name: `${translate('royal')}`,
      image: Royal,
      bg: RoyalBackground,
      cardBackground: RoyalCardBackground,
      maintenanceRequirementValue: 800000,
      maintenanceRequirementMax: 1000000,
      subColor: '#C5C5C5',
      titleColor: ['#F5F5F5', '#969696', '#7F7F7F'],
      details: {
        dailyRebate: '0.8',
        withdrawCount: `${translate('unlimited')}`,
        weeklyRebate: '4.5',
        tierBonus: '2000',
        birthdayGift: `${translate('yes')}`,
        birthdayBonus: '1,288',
        upgradeNext: '1,000,000',
        renewal: `3 ${translate('months')}`,
      },
    },
  ];

  const current = VIP_LEVELS[activeIndex];

  const carouselRef = useRef(null);

  return (
    <>
      {isDesktop && (
        <>
          <IconButton
            onClick={() => carouselRef.current?.slickPrev()}
            sx={{
              position: 'fixed',
              top: '50%',
              left: 'calc(50% - 300px)',
              transform: 'translateY(-50%)',
              zIndex: 2000,
              width: 44,
              height: 44,
              padding: 0,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.12)',
              backdropFilter: 'blur(6px)',
              color: '#fff',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.25)',
              },
            }}
          >
            <Iconify icon="mdi:chevron-left" width={28} />
          </IconButton>

          <Dialog
            open={handleOpen}
            onClose={handleClose}
            maxWidth="xs"
            scroll="body"
            PaperProps={{
              sx: {
                maxHeight: '90vh',
                backgroundColor: '#000',
                overflow: 'hidden',
                borderBottomLeftRadius: 20,
                borderBottomRightRadius: 20,
                borderTopLeftRadius: 20,
                borderTopRightRadius: 20,
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  display: 'none',
                },

                scrollbarWidth: 'none',

                msOverflowStyle: 'none',
              },
            }}
          >
            <Box sx={{ backgroundColor: '#000', position: 'relative' }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  pt: 2,
                  px: 5,
                  pb: 5,
                  width: '100%',
                }}
              >
                <Typography variant="subtitle1" fontWeight={600} textAlign="center" mb={1} color={'#ffff'}>
                  {translate('vip_center')}
                </Typography>

                <Iconify
                  icon="mdi:close"
                  width={24}
                  sx={{
                    color: '#fff',
                    position: 'absolute',
                    top: 20,
                    right: 20,
                    cursor: 'pointer',
                    zIndex: 10,
                  }}
                  onClick={handleClose}
                />
              </Box>

              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: 260,
                  backgroundImage: `url(${current.bg})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'top',
                  zIndex: 0,
                  transition: '0.4s ease',
                }}
              />

              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: 260,
                  background: 'linear-gradient(to bottom, rgba(0,0,0,0.2), #000)',
                  zIndex: 1,
                }}
              />

              <Box sx={{ position: 'relative', zIndex: 2 }}>
                <VipCarousel
                  currentIndex={activeIndex}
                  setCurrentIndex={setActiveIndex}
                  cards={VIP_LEVELS}
                  carouselRef={carouselRef}
                />
              </Box>
            </Box>
          </Dialog>

          <IconButton
            onClick={() => carouselRef.current?.slickNext()}
            sx={{
              position: 'fixed',
              top: '50%',
              right: 'calc(50% - 300px)',
              transform: 'translateY(-50%)',
              zIndex: 2000,
              width: 44,
              height: 44,
              padding: 0,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.12)',
              backdropFilter: 'blur(6px)',
              color: '#fff',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.25)',
              },
            }}
          >
            <Iconify icon="mdi:chevron-right" width={28} />
          </IconButton>
        </>
      )}

      {!isDesktop && (
        <Drawer
          anchor="right"
          open={handleOpen}
          onClose={handleClose}
          PaperProps={{
            sx: {
              width: '100%',
              bgcolor: '#000',
              position: 'relative',
              overflow: 'hidden',
            },
          }}
        >
          {/* Background */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: 260,
              backgroundImage: `url(${current.bg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'top',
              zIndex: 0,
              transition: '0.4s ease',
            }}
          />

          {/* Gradient overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: 260,
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.2), #000)',
              zIndex: 1,
            }}
          />

          <Box sx={{ position: 'relative', zIndex: 2, height: '100%' }}>
            {/* Header */}
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 2,
              }}
            >
              <IconButton onClick={handleClose} sx={{ position: 'absolute', left: 8 }}>
                <Iconify icon="mdi:arrow-left" width={24} sx={{ color: '#fff' }} />
              </IconButton>

              <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: 18 }}>{translate('vip_center')}</Typography>
            </Box>

            <VipCarousel currentIndex={activeIndex} setCurrentIndex={setActiveIndex} cards={VIP_LEVELS} />
          </Box>
        </Drawer>
      )}
    </>
  );
};

export default Vip;
